import { ExternalLink, Mail } from "lucide-react";

const PARTNER_EMAIL = "partners@humanhomeliving.com";

/** Published cost ranges with the source and date beside every figure. Never our own estimates. */
export const CostGuide = ({ costs, testId }) => (
  <section
    id="costs"
    data-testid={testId}
    className="mt-16 md:mt-20 border-t border-line pt-12 scroll-mt-28"
  >
    <span className="eyebrow">Published cost ranges</span>
    <h2 className="display text-[29px] md:text-[37px] leading-[1.1] text-charcoal mt-5 max-w-2xl">
      What published sources say it costs
    </h2>
    <p className="text-[16.5px] leading-[1.65] text-[#3F3F3F] mt-5 max-w-3xl">{costs.intro}</p>

    <div className="mt-9 overflow-x-auto">
      <table className="w-full min-w-[640px] border-collapse text-left">
        <thead>
          <tr className="border-b border-charcoal/20">
            <th className="py-3 pr-5 text-[11px] tracking-[0.16em] uppercase font-semibold text-charcoal">
              Type
            </th>
            <th className="py-3 pr-5 text-[11px] tracking-[0.16em] uppercase font-semibold text-charcoal">
              Equipment note
            </th>
            <th className="py-3 pr-5 text-[11px] tracking-[0.16em] uppercase font-semibold text-charcoal">
              Published figure
            </th>
            <th className="py-3 text-[11px] tracking-[0.16em] uppercase font-semibold text-charcoal">
              Source &amp; date
            </th>
          </tr>
        </thead>
        <tbody>
          {costs.rows.map((r) => (
            <tr key={`${r.label}-${r.source}`} className="border-b border-line align-top">
              <td className="py-4 pr-5 text-[15px] text-charcoal font-medium">{r.label}</td>
              <td className="py-4 pr-5 text-[14.5px] text-[#4F4B45]">{r.equipment}</td>
              <td className="py-4 pr-5 text-[15px] text-sage-dark font-semibold whitespace-nowrap">
                {r.installed}
              </td>
              <td className="py-4 text-[13.5px] text-[#6E6A63]">{r.source}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>

    <div className="grid md:grid-cols-2 gap-x-12 gap-y-7 mt-10">
      <div>
        <h3 className="text-[16.5px] font-semibold text-charcoal">
          Equipment cost versus installation
        </h3>
        <p className="text-[15.5px] leading-[1.65] text-[#4F4B45] mt-2.5">{costs.includes}</p>
      </div>
      <div>
        <h3 className="text-[16.5px] font-semibold text-charcoal">Insurance and coverage</h3>
        <p className="text-[15.5px] leading-[1.65] text-[#4F4B45] mt-2.5">{costs.coverageNote}</p>
      </div>
    </div>

    <div className="mt-9 border-l-2 border-sage pl-5">
      <h3 className="text-[13px] tracking-[0.1em] uppercase font-semibold text-charcoal">
        Sources
      </h3>
      <ul className="mt-3 space-y-2">
        {costs.sources.map((s) => (
          <li key={s.url} className="text-[14.5px] leading-[1.6] text-[#4F4B45]">
            <a
              href={s.url}
              target="_blank"
              rel="noopener noreferrer nofollow"
              data-testid={`mm-cost-source-${s.name.toLowerCase().replace(/\s+/g, "-")}`}
              className="inline-flex items-center gap-1.5 text-charcoal underline decoration-sage decoration-2 underline-offset-[5px] hover:text-sage-dark transition-colors"
            >
              {s.name} — {s.title}
              <ExternalLink size={13} className="shrink-0" />
            </a>
            <span className="text-[#6E6A63]"> ({s.dated})</span>
          </li>
        ))}
      </ul>
      <p className="text-[13.5px] leading-[1.6] text-[#6E6A63] mt-4">
        Figures are reproduced exactly as each source published them on the date shown. Published
        ranges are a starting point for planning, never a quotation for your home — check the source
        for its current figure before relying on it.
      </p>
    </div>
  </section>
);

export const QuestionsToAsk = ({ questions, heading, testId }) => (
  <section
    id="questions-to-ask"
    data-testid={testId}
    className="mt-16 md:mt-20 border-t border-line pt-12 scroll-mt-28"
  >
    <span className="eyebrow">Before you commit</span>
    <h2 className="display text-[29px] md:text-[37px] leading-[1.1] text-charcoal mt-5 max-w-2xl">
      {heading}
    </h2>
    <ol className="grid md:grid-cols-2 gap-x-12 gap-y-4 mt-9 list-none counter-reset">
      {questions.map((q, i) => (
        <li key={q} className="flex gap-3.5 text-[15.5px] leading-[1.6] text-[#3F3F3F]">
          <span className="text-[13px] font-semibold text-sage-dark pt-[3px]">
            {String(i + 1).padStart(2, "0")}
          </span>
          <span>{q}</span>
        </li>
      ))}
    </ol>
  </section>
);

/** Kept visually distinct from buying guidance: this speaks to companies, not to shoppers. */
export const PartnershipInvite = ({ variant = "short", testId = "mm-partnership" }) => (
  <aside
    data-testid={testId}
    className="mt-16 md:mt-20 border border-sage/45 bg-[#F4F6F2] px-6 md:px-9 py-8 md:py-10"
  >
    <span className="text-[11px] tracking-[0.18em] uppercase text-sage-dark font-semibold">
      For industry
    </span>
    {variant === "full" ? (
      <>
        <h2 className="display text-[26px] md:text-[33px] leading-[1.14] text-charcoal mt-4 max-w-2xl">
          Interested in Featuring Your Products on Human Home Living?
        </h2>
        <p className="text-[15.5px] leading-[1.68] text-[#454545] mt-4 max-w-3xl">
          Human Home Living welcomes partnership inquiries from manufacturers, retailers, and
          service providers offering stair lifts, residential elevators, mobility equipment,
          accessible vehicles, and other products that support independent living.
        </p>
        <p className="text-[15.5px] leading-[1.68] text-[#454545] mt-3.5 max-w-3xl">
          We are interested in affiliate partnerships, qualified-lead referral programs, and other
          performance-based arrangements that provide transparent tracking and compensation.
        </p>
        <p className="text-[15.5px] leading-[1.68] text-[#454545] mt-3.5 max-w-3xl">
          If your company would like to be considered for inclusion in our Medical &amp; Mobility
          resources, please contact our partnerships team.
        </p>
      </>
    ) : (
      <>
        <h2 className="display text-[23px] md:text-[27px] leading-[1.16] text-charcoal mt-4 max-w-2xl">
          Industry Partnership Opportunities
        </h2>
        <p className="text-[15.5px] leading-[1.68] text-[#454545] mt-4 max-w-3xl">
          Are you a manufacturer, retailer, or service provider interested in partnering with Human
          Home Living? We welcome inquiries about affiliate programs and qualified-lead referral
          partnerships.
        </p>
      </>
    )}
    <a
      href={`mailto:${PARTNER_EMAIL}`}
      data-testid={`${testId}-email`}
      className="inline-flex items-center gap-2.5 mt-6 px-6 py-3.5 text-[12px] tracking-[0.13em] uppercase font-semibold bg-sage text-white hover:bg-sage-dark transition-colors duration-300"
    >
      <Mail size={15} /> {PARTNER_EMAIL}
    </a>
    {variant === "full" && (
      <p className="text-[13.5px] leading-[1.6] text-[#6E6A63] mt-5 max-w-3xl">
        Product inclusion is subject to editorial review. Partnership inquiries do not guarantee
        placement or endorsement.
      </p>
    )}
  </aside>
);
